import { useEffect, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, Dateityp, DateitypAnlegen } from "@kundeninfo/api-client";
import { darfAbbrechen, Feld, Modal, ModalFuss, useBeforeUnload } from "@kundeninfo/ui";
import { KEINE_ZAHL, Meldung, alsZahl, feldFehlerAus, meldungAus } from "./gemeinsam.js";
import "../Verwaltung.css";

/**
 * Der Verwaltungsbereich DATEI-KATEGORIEN (fileType, Paket 4).
 *
 * Herausgelöst aus views/Verwaltung.tsx (Etappe 2, Aufgabe 3) — rein strukturell, CSS und
 * Verhalten unverändert (Block `vw__`, views/Verwaltung.css).
 *
 * **Ohne zweite Ebene** (Entscheid Aufgabe 3, dieselbe Begründung wie bei den
 * Ansprechpartner-Rollen): Eine Kategorie trägt Name, Beschreibung, Reihenfolge und Aktiv —
 * die Listenzeile zeigt alle vier. Eine Spaltenliste davor verlangte einen Klick, um
 * dasselbe noch einmal zu sehen, und zerschnitte die REIHENFOLGE, die man nur im Ganzen
 * beurteilen kann.
 *
 * Anders als der Rollen- oder Konditionsbereich trägt `fileType` KEINE `version`
 * (Ruling P4-B): Es gibt deshalb keinen Versionsvergleich und keine Konfliktauflösung —
 * die letzte Änderung gewinnt. Das Stilllegen läuft über einen eigenen Klick
 * (`setFileTypeActive`), damit ein Umschalten nicht den ganzen Feldsatz erneut schickt und
 * dabei am eindeutigen Namen anecken kann.
 */

/** Nur die vier Wege dieses Bereichs — so lässt er sich im Test mit vier vi.fn zeichnen. */
export type DateitypenClient = Pick<ApiClient,
  "listFileTypes" | "createFileType" | "updateFileType" | "setFileTypeActive">;

type DateitypDialog = {
  /** null = neu anlegen. */
  id: string | null;
  name: string;
  description: string;
  sortIndex: string;
  active: boolean;
};

const LEERER_DATEITYPDIALOG: DateitypDialog =
  { id: null, name: "", description: "", sortIndex: "0", active: true };

type DateitypFeld = "name" | "description" | "sortIndex";

/** Wie rollendialogAus, für die Kategorien. `description = null` wird zur leeren Eingabe. */
function dateitypdialogAus(t: Dateityp | null): DateitypDialog {
  if (t === null) return LEERER_DATEITYPDIALOG;
  return { id: t.id, name: t.name, description: t.description ?? "",
           sortIndex: String(t.sortIndex), active: t.active };
}

function dateitypdialogGleich(a: DateitypDialog, b: DateitypDialog): boolean {
  return a.id === b.id && a.name === b.name && a.description === b.description
    && a.sortIndex === b.sortIndex && a.active === b.active;
}

export function DateitypenBereich(
  { client }: { client: DateitypenClient },
): ReactElement {
  const [zeilen, setZeilen] = useState<Dateityp[] | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [dialog, setDialog] = useState<DateitypDialog | null>(null);
  const [feldFehler, setFeldFehler] = useState<Partial<Record<DateitypFeld, string>>>({});
  const [meldung, setMeldung] = useState<string | null>(null);
  const [arbeitet, setArbeitet] = useState(false);
  /** Die Zeile, deren Aktiv-Zustand gerade umgeschaltet wird (Knopf sperren). */
  const [schaltet, setSchaltet] = useState<string | null>(null);

  const listFileTypes = client.listFileTypes;

  useEffect(() => {
    let abgebrochen = false;
    listFileTypes()
      .then((antwort) => { if (!abgebrochen) setZeilen(antwort.items); })
      .catch((fehler: unknown) => {
        if (!abgebrochen) {
          setLadeFehler(
            meldungAus(fehler, "Die Datei-Kategorien konnten nicht geladen werden."));
        }
      });
    return () => { abgebrochen = true; };
  }, [listFileTypes]);

  // 2a-Kleinpunkt, wie im Rollenbereich.
  const dialogGeaendert = dialog !== null && !dateitypdialogGleich(
    dialog, dateitypdialogAus(zeilen?.find((z) => z.id === dialog.id) ?? null));
  useBeforeUnload(dialogGeaendert);

  /** Der eine Weg aus dem Modal neben dem Speichern (Hausregel „striktes Modalpanel",
   *  06.09.2026) — mit Rückfrage, wenn etwas Ungespeichertes darin steht. Escape ruft
   *  denselben Weg (siehe `Modal` in @kundeninfo/ui); ein Klick daneben schliesst nicht. */
  function abbrechen(): void {
    // Kein Ausgang, solange geschrieben wird — das gilt für die ESCAPE-Taste genauso wie
    // für den (dann gesperrten) Knopf: Beide gehen durch diese Funktion, und die Antwort
    // des Servers braucht ihr Panel noch (Fixrunde 1).
    if (arbeitet) return;
    if (!darfAbbrechen(dialogGeaendert)) return;
    setDialog(null);
  }

  function oeffnen(t: Dateityp | null): void {
    setFeldFehler({});
    setMeldung(null);
    setDialog(dateitypdialogAus(t));
  }

  function eingabeAus(d: DateitypDialog): DateitypAnlegen | null {
    const sortIndex = alsZahl(d.sortIndex);
    if (sortIndex === KEINE_ZAHL || sortIndex === null) {
      setFeldFehler({ sortIndex: "Bitte eine Zahl eintragen." });
      return null;
    }
    // Leere Beschreibung ausdrücklich als null — die Feinprüfung (Trimmen, Länge) macht das
    // Repository; hier reicht die Umwandlung „nichts eingetippt = keine Beschreibung".
    return {
      name: d.name,
      description: d.description.trim() === "" ? null : d.description,
      sortIndex,
      active: d.active,
    };
  }

  async function speichern(): Promise<void> {
    const d = dialog;
    if (d === null) return;
    setFeldFehler({});
    setMeldung(null);
    const eingabe = eingabeAus(d);
    if (eingabe === null) return;
    setArbeitet(true);
    try {
      if (d.id === null) {
        // Anlegen antwortet nur mit der Kennung — die Liste neu lesen (sie ist nach
        // sortIndex geordnet, der neue Eintrag gehört an seinen Platz).
        await client.createFileType(eingabe);
        const antwort = await client.listFileTypes();
        setZeilen(antwort.items);
      } else {
        // Ohne version (Ruling P4-B): der Aufruf trägt nur die Kennung und den Feldsatz.
        const neu = await client.updateFileType(d.id, eingabe);
        setZeilen((alt) => (alt === null ? alt
          : alt.map((x) => (x.id === neu.id ? neu : x))
              .sort((a, b) => a.sortIndex - b.sortIndex)));
      }
      setDialog(null);
    } catch (fehler) {
      if (fehler instanceof ApiError && fehler.status === 422) {
        const { felder, unzuordenbar } = feldFehlerAus(
          fehler, ["name", "description", "sortIndex"]);
        setFeldFehler(felder);
        if (unzuordenbar || Object.keys(felder).length === 0) setMeldung(fehler.message);
      } else {
        setMeldung(meldungAus(fehler, "Unerwarteter Fehler beim Speichern."));
      }
    } finally {
      setArbeitet(false);
    }
  }

  async function umschalten(t: Dateityp): Promise<void> {
    setMeldung(null);
    setSchaltet(t.id);
    try {
      const neu = await client.setFileTypeActive(t.id, !t.active);
      setZeilen((alt) => (alt === null ? alt : alt.map((x) => (x.id === neu.id ? neu : x))));
    } catch (fehler) {
      setMeldung(meldungAus(fehler, "Der Zustand konnte nicht geändert werden."));
    } finally {
      setSchaltet(null);
    }
  }

  if (ladeFehler !== null) return <Meldung text={ladeFehler} />;
  if (zeilen === null) return <p className="vw__hinweis">Lädt …</p>;

  const sortiert = [...zeilen].sort((a, b) => a.sortIndex - b.sortIndex);

  return (
    <>
      {meldung !== null && dialog === null && <Meldung text={meldung} />}
      <div className="vw__leiste">
        <button type="button" className="vw__primaer" onClick={() => oeffnen(null)}>
          Neue Kategorie
        </button>
      </div>

      <ul className="vw__liste">
        {sortiert.map((t) => (
          <li key={t.id} className="vw__eintrag">
            <div className="vw__eintrag-kopf">
              <span className="vw__eintrag-name">{t.name}</span>
              {!t.active && <span className="vw__marke">stillgelegt</span>}
            </div>
            {t.description !== null && t.description !== "" && (
              <span className="vw__eintrag-wert">{t.description}</span>
            )}
            <span className="vw__eintrag-zusatz">Reihenfolge {t.sortIndex}</span>
            <div className="vw__eintrag-aktionen">
              <button type="button" className="vw__knopf" aria-label={`${t.name} ändern`}
                onClick={() => oeffnen(t)}>
                Ändern
              </button>
              <button type="button" className="vw__knopf" disabled={schaltet === t.id}
                aria-label={t.active
                  ? `${t.name} stilllegen` : `${t.name} wieder einschalten`}
                onClick={() => { void umschalten(t); }}>
                {t.active ? "Stilllegen" : "Wieder einschalten"}
              </button>
            </div>
          </li>
        ))}
      </ul>

      {dialog !== null && (
        <Modal titel={dialog.id === null ? "Neue Kategorie" : "Kategorie ändern"}
          onAbbrechen={abbrechen} abbrechenGesperrt={arbeitet}>
          {meldung !== null && <Meldung text={meldung} />}
          <Feld id="vw-d-name" label="Name" bearbeitbar wert={dialog.name}
            onChange={(w) => setDialog((d) => (d === null ? d : { ...d, name: w }))}
            {...(feldFehler.name !== undefined ? { fehler: feldFehler.name } : {})} />
          <Feld id="vw-d-description" label="Beschreibung (optional)" bearbeitbar
            wert={dialog.description}
            onChange={(w) => setDialog((d) => (d === null ? d : { ...d, description: w }))}
            {...(feldFehler.description !== undefined
              ? { fehler: feldFehler.description } : {})} />
          <Feld id="vw-d-sortIndex" label="Reihenfolge" bearbeitbar wert={dialog.sortIndex}
            onChange={(w) => setDialog((d) => (d === null ? d : { ...d, sortIndex: w }))}
            {...(feldFehler.sortIndex !== undefined
              ? { fehler: feldFehler.sortIndex } : {})} />
          <label className="vw__schalter">
            <input type="checkbox" checked={dialog.active}
              onChange={(e) => setDialog((d) => (d === null ? d
                : { ...d, active: e.target.checked }))} />
            <span>Aktiv (steht zur Auswahl)</span>
          </label>
          <ModalFuss speichernText="Speichern" speichernGesperrt={arbeitet}
            abbrechenGesperrt={arbeitet}
            onSpeichern={() => { void speichern(); }} onAbbrechen={abbrechen} />
        </Modal>
      )}
    </>
  );
}
